import { create } from "zustand";
import { hasMapPosition } from "../../utils/hasMapPosition";
import { trackDiagnostic } from "../../utils/diagnostics";
import {
  connectSocket,
  disconnectSocket,
  subscribeToDevices,
  getSocket,
  getLastPositionBatch,
  getLastPosition,
  getTripStatus,
  SOCKET_MAX_RECONNECT_ATTEMPTS,
} from "../../socket";
import {
  type DevicesLoadErrorCode,
} from "../../features/devices/devicesDiagnostics";
import { useCompaniesStore } from "./companiesStore";
import { useDeviceEventsStore } from "./deviceEventsStore";
import { useDeviceCommandsStore } from "./deviceCommandsStore";
import { useTripTrackingStore } from "./tripTrackingStore";
import { resolveMetricsFromParams } from "../../utils/vehicleParamsResolver";
import {
  IDLE_CONFIG,
  determineVehicleStatus,
  resolveEffectiveSpeed,
  odometerKmFromParams,
} from "../shared/vehicleStatusRule";

// Interfaz para los datos que llegan del socket
export interface PositionFromSocket {
  device_id: string;
  company_id: string;
  vehicle_id: string;
  remolque_id: string;
  imei: string;
  brand_model?: string | null;
  brand_name?: string | null;
  timeStamp: number;
  lat: number;
  long: number;
  heading: number;
  rawParams?: Record<string, number | string | null> | null;
  battery: number | null;
  altitude: number;
  event_code: number;
  speed?: number | null;
  // Ignición reportada fuera de params (puede venir o no; true/false).
  ignition?: boolean | null;
  params: {
    panic: number | null;
    engine_status: number | null;
    event_code: number;
    gps_sat?: number;
    odo_km: number | null;
    provider: string;
    rssi?: number;
    sim?: string;
    dallas_temperature?: number;
    fuel?: number | null;
    engine_hours?: number | null;
  };
}

// Interfaz del vehículo para la UI (compatible con VehicleRealtime)
export interface VehicleSocket {
  id: string;
  fullId: string;
  status: string;
  driver: {
    id?: string;
    name: string;
    licenseId: string;
    photo: string;
    experience: string;
    phone: string;
    email?: string;
  };
  vehicle: {
    brand: string;
    model: string;
    year: number;
    plates: string;
    color: string;
    name: string;
    type: string;
    /** Carrocería del vehículo en Administración (car, truck, pickup…). */
    bodyType?: string;
    /** Modelo de puntero elegido (config/vehicleModels); vacío = por omisión. */
    pointerModel?: string;
  };
  coordinates: string;
  latitude: number;
  longitude: number;
  battery: number | null;
  signal: number;
  lastReport: {
    _seconds: number;
    _nanoseconds: number;
  };
  speed: number | null;
  fuel: number | null;
  temperature: number;
  odometer: number | null;
  engineHours: number | null;
  group: string;
  icon: string;
  imei?: string;
  companyId: string;
  altitude: number;
  course: number;
  sensors: {
    [key: string]: string | number;
  };
  ignition: boolean;
  socketParams?: Record<string, number | string>;
  humidity: number;
  eventCode: number;
  gps_sat: number | null;
  engineStatus: number | null;
  serialNumber: string;
  shipment: string;
  engineCutoff: boolean | null;
  hitch: boolean | null;
  params: {
    panic: number | null;
    datetime: string;
    engineStatus: string;
    imei: string;
    latitude: number;
  };
  device: {
    type: string;
    id: string;
    mileage: number | null;
    brand_id?: string;
  };
  reference: string;
  h3: Record<string, string>;
  trip: string;
  vehicle_id: string;
  remolque_id: string;
  address: string;
  brandModel?: string;
  brandName?: string;
  idleSince?: number | null;
}

// Datos iniciales de vehículos (obtenidos de la API/devices)
export interface DeviceData {
  id: string;
  fullId?: string;
  companyId: string;
  identifier?: string;
  model?: string;
  status_id?: string;
  type?: string;
  vehicle_id?: string;
  remolque_id?:string;
  serial?: string;
  sim_card?: string;
  brand_id?: string;
  vehicle?: {
    id?: string;
    brand?: string;
    model?: string;
    year?: number;
    plates?: string;
    plate?: string; // API puede enviar "plate" en lugar de "plates"
    color?: string;
    name?: string;
    type?: string;
    pointer_model?: string;
  };
  driver?: {
    id?: string;
    name: string;
    licenseId?: string;
    photo?: string;
    experience?: string;
    phone?: string;
    email?: string;
  };
  group?: string;
  icon?: string;
  imei?: string;
  // Última posición conocida del device (viene de la API)
  lastPosition?: {
    lat?: number;
    lon?: number;
    speed?: number | null;
    heading?: number | null;
    timeStamp?: number;
    altitude?: number | null;
    accuracy?: number | null;
    battery?: number | null;
    ignition?: boolean;
    event_code?: number;
    params?: {
      engine_status?: number;
      event_code?: number;
      odo_km?: number;
      fuel?: number | null;
      engine_hours?: number | null;
    };
  };
}

interface VehiclesSocketStore {
  // Estado de vehículos
  vehicles: VehicleSocket[];
  loading: boolean;
  error: string | null;
  loadError: DevicesLoadErrorCode | null;
  reloadRequestId: number;
  isConnected: boolean;
  lastReconnect: Date | null;

  // Estado de inicialización
  isInitialized: boolean;
  lastInitializedCompanyId: string | null;
  awaitingCompanySelection: boolean;
  isLoadingInitialPositions: boolean;
  initialSyncDone: boolean;
  pendingDevicesData: DeviceData[];

  // Device IDs para gestión de suscripciones
  allCompanyDeviceIds: string[];
  currentSubscribedDeviceIds: string[];

  // Búsqueda
  searchQuery: string;
  debouncedSearchQuery: string;
  setSearchQuery: (query: string) => void;
  clearSearch: () => void;

  // Filtros
  selectedStatus: string | null;
  selectedGroup: string;
  selectedCompany: string | null;
  setSelectedStatus: (status: string | null) => void;
  setSelectedGroup: (group: string) => void;
  setSelectedCompany: (company: string | null) => void;
  clearFilters: () => void;

  // Vehículo enfocado en mapa
  focusedVehicleId: string | null;
  // Sube con cada `notification` del socket (dumax-socket → user:<uid>). La
  // campana del mapa lo observa para refrescar su bandeja: el listener vive
  // aquí porque connect() hace removeAllListeners() y borraría uno externo.
  unitNotificationTick: number;
  setFocusedVehicleId: (id: string | null) => void;

  // Vehículo resaltado temporalmente
  highlightedVehicleId: string | null;
  setHighlightedVehicleId: (id: string | null, duration?: number) => void;

  // Loading de address por device (Record para O(1) lookup)
  loadingAddressDeviceIds: Record<string, boolean>;
  requestAddressForDevice: (deviceId: string) => void;

  // Loading de posición inicial por device (para mostrar skeleton en cards)
  loadingPositionDeviceIds: Record<string, boolean>;

  // Map para lookups O(1) de vehículos por ID
  vehiclesMap: Map<string, number>;

  // Tick global para actualizar tiempos (1 intervalo para todas las cards)
  timeTick: number;
  startTimeTick: () => void;
  stopTimeTick: () => void;

  // Acciones del socket
  initializeWithDevices: (devices: DeviceData[]) => void;
  finalizeInitialLoad: (positions: Map<string, PositionFromSocket>) => void;
  connect: (deviceIds: string[]) => void;
  disconnect: () => void;
  resubscribe: (deviceIds: string[]) => void;
  updateVehiclePosition: (position: PositionFromSocket) => void;
  updateVehiclePositionsBatch: (positions: PositionFromSocket[]) => void;
  updateParameters: (parameters: any) => void;
  updateEvent: (event: any) => void;
  setConnectionStatus: (status: boolean) => void;
  setAwaitingCompanySelection: (awaiting: boolean) => void;
  setLoading: (loading: boolean) => void;
  setLoadError: (loadError: DevicesLoadErrorCode | null) => void;
  requestReload: () => void;
  resetStore: () => void;
  updateDriver: (device_id: string, driver: VehicleSocket["driver"]) => void;
  updateVehicle: (device_id: string, vehicle: VehicleSocket["vehicle"]) => void;
  updateRemolque: (device_id: string, remolque_id: string | null) => void;
  refreshDevicesData: (devices: DeviceData[]) => void;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let highlightTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectInterval: ReturnType<typeof setInterval> | null = null;
let timeTickInterval: ReturnType<typeof setInterval> | null = null;

// Acumulador de posiciones durante la carga inicial
let initialLoadPositions: Map<string, PositionFromSocket> = new Map();
let isInitialLoadPhase = false;

const MAX_SERVER_DISCONNECT_RETRIES = 5;
let socketConnectErrorCount = 0;
let serverDisconnectCount = 0;
let reconnectFailedHandler: (() => void) | null = null;

// Tiempo de reconexión: 55 minutos (antes de que expire la hora)
const RECONNECT_INTERVAL = 55 * 60 * 1000;
// Tiempo de espera para que lleguen las posiciones del socket antes de finalizar la carga inicial
const INITIAL_LOAD_TIMEOUT = 2000;
const OFFLINE_THRESHOLD_MS = 60 * 60 * 1000;

// Función helper para manejar timestamps de forma segura
// El servidor envía timestamps en milisegundos
const safeTimestamp = (timestamp: number | null | undefined): number => {
  if (!timestamp || timestamp <= 0) {
    return Date.now();
  }
  return timestamp;
};

// Convertir timestamp de milisegundos a segundos
const timestampToSeconds = (timestamp: number | null | undefined): number => {
  const ts = safeTimestamp(timestamp);
  // Si el timestamp es muy grande (milisegundos), convertir a segundos
  if (ts > 9999999999) {
    return Math.floor(ts / 1000);
  }
  return ts;
};

// Crear fecha ISO de forma segura
const safeToISOString = (timestamp: number | null | undefined): string => {
  try {
    const ts = safeTimestamp(timestamp);
    return new Date(ts).toISOString();
  } catch {
    return new Date().toISOString();
  }
};

const mergeSocketParams = (
  existing: Record<string, number | string> | undefined,
  incoming: Record<string, number | string | null> | null | undefined
): { params: Record<string, number | string>; changed: boolean } => {
  const base = existing ?? {};
  if (!incoming || typeof incoming !== "object") {
    return { params: base, changed: false };
  }

  let changed = false;
  let merged: Record<string, number | string> = base;

  for (const key in incoming) {
    const value = incoming[key];
    if (value === null || value === undefined) continue;
    if (typeof value !== "number" && typeof value !== "string") continue;
    if (base[key] !== value) {
      if (merged === base) merged = { ...base };
      merged[key] = value;
      changed = true;
    }
  }

  return { params: merged, changed };
};

const SOCKET_PANIC_EVENT_CODES = new Set<number>([5]);

const derivePanic = (
  rawPanic: number | null | undefined,
  eventCode: number | null | undefined
): number | null => {
  if (rawPanic != null) return Number(rawPanic);
  const code = eventCode != null ? Number(eventCode) : null;
  if (code != null && SOCKET_PANIC_EVENT_CODES.has(code)) return code;
  return null;
};

interface ResolvedVehicleMetrics {
  battery: number | null;
  gpsSat: number | null;
  odometer: number | null;
  ignition: boolean;
  ignitionState: boolean | null;
  idle: boolean | null;
  panic: number | null;
  statusEngineStatus: number | null | undefined;
  brandModel: string;
  brandName: string;
  engineCutoff: boolean | null;
  hitch: boolean | null;
}

const resolveVehicleMetrics = (
  position: PositionFromSocket,
  mergedParams: Record<string, number | string> | undefined,
  existing?: VehicleSocket
): ResolvedVehicleMetrics => {
  const brandModel = position.brand_model ?? existing?.brandModel ?? "";
  const brandName = position.brand_name ?? existing?.brandName ?? "";
  const resolved = resolveMetricsFromParams(mergedParams, brandModel, brandName);

  const directEngine = position.params?.engine_status;
  const rawIgnition = position.ignition;
  // Motor encendido si engine_status ∈ {1,2,3} O ignition === true (OR).
  const directIgnition =
    directEngine != null || rawIgnition != null
      ? directEngine === 1 ||
        directEngine === 2 ||
        directEngine === 3 ||
        rawIgnition === true
      : null;

  const directPanic = derivePanic(position.params?.panic, position.event_code);

  const ignitionState = directIgnition ?? resolved.ignition;

  const statusEngineStatus =
    directEngine != null
      ? directEngine
      : rawIgnition === true
        ? 1
        : rawIgnition === false
          ? 4
          : resolved.ignition === true
            ? 1
            : resolved.ignition === false
              ? 4
              : undefined;

  return {
    battery: position.battery ?? resolved.battery ?? existing?.battery ?? null,
    gpsSat: position.params?.gps_sat ?? resolved.satellites ?? existing?.gps_sat ?? null,
    odometer: odometerKmFromParams(mergedParams) ?? existing?.odometer ?? null,
    ignition: ignitionState ?? existing?.ignition ?? false,
    ignitionState,
    idle: resolved.idle,
    panic: directPanic ?? resolved.sos,
    statusEngineStatus,
    brandModel,
    brandName,
    engineCutoff: resolved.engineCutoff ?? existing?.engineCutoff ?? null,
    hitch: resolved.hitch ?? existing?.hitch ?? null,
  };
};

// Función para crear un vehículo desde DeviceData
const createVehicleFromDevice = (device: DeviceData): VehicleSocket => {
  // Extraer datos de lastPosition si existen
  const lastPos = device.lastPosition;
  const hasLastPosition = lastPos && (lastPos.lat != null && lastPos.lon != null);

  // Extraer engine_status de params o del nivel superior
  const engineStatus = lastPos?.params?.engine_status ?? null;
  const odoKm = lastPos?.params?.odo_km ?? null;

  // Convertir timestamp de lastPosition (viene en milisegundos)
  let lastReportSeconds = 0;
  if (lastPos?.timeStamp) {
    lastReportSeconds = lastPos.timeStamp > 9999999999
      ? Math.floor(lastPos.timeStamp / 1000)
      : lastPos.timeStamp;
  }

  // Motor encendido si engine_status ∈ {1,2,3} O ignition === true (OR).
  const initialIgnition =
    lastPos?.ignition === true ||
    engineStatus === 1 ||
    engineStatus === 2 ||
    engineStatus === 3;

  // Determinar estado basado en lastPosition si existe
  let initialStatus = "offline";
  let initialIdleSince: number | null = null;
  if (hasLastPosition && lastPos) {
    const initial = determineVehicleStatus(
      lastPos.speed,
      initialIgnition,
      lastPos.timeStamp,
      "offline"
    );
    initialStatus = initial.status;
    initialIdleSince = initial.idleSince;
  }

  // Extraer plates (la API puede enviar "plate" o "plates")
  const vehiclePlates = device.vehicle?.plates || device.vehicle?.plate || "";

  // Determinar tipo de unidad por endpoint: remolque_id → trailer, vehicle_id → vehículo
  const unitType = device.remolque_id ? "remolque" : device.vehicle_id ? "vehicle" : "";

  return {
    id: device.id,
    fullId: device.fullId || device.identifier || device.id,
    status: initialStatus,
    driver: device.driver ? {
      id: device.driver.id,
      name: device.driver.name || "Sin asignar",
      licenseId: device.driver.licenseId || "",
      photo: device.driver.photo || "",
      experience: device.driver.experience || "",
      phone: device.driver.phone || "",
      email: device.driver.email,
    } : {
      name: "Sin asignar",
      licenseId: "",
      photo: "",
      experience: "",
      phone: "",
    },
    vehicle: device.vehicle ? {
      brand: device.vehicle.brand || "",
      model: device.vehicle.model || device.model || "",
      year: device.vehicle.year || 0,
      plates: vehiclePlates,
      color: device.vehicle.color || "",
      name: device.vehicle.name || device.fullId || device.id,
      type: unitType,
      bodyType: device.vehicle.type || "",
      pointerModel: device.vehicle.pointer_model || "",
    } : {
      brand: "",
      model: device.model || "",
      year: 0,
      plates: "",
      color: "",
      name: device.fullId || device.identifier || device.id,
      type: unitType,
    },
    address: "",
    // Solo establecer coordenadas si hay lastPosition válida con lat y lon
    coordinates: hasLastPosition && lastPos ? `${lastPos.lat},${lastPos.lon}` : "",
    latitude: hasLastPosition && lastPos?.lat != null ? lastPos.lat : 0,
    longitude: hasLastPosition && lastPos?.lon != null ? lastPos.lon : 0,
    battery: lastPos?.battery ?? null,
    signal: 0,
    lastReport: {
      _seconds: lastReportSeconds,
      _nanoseconds: 0,
    },
    speed: lastPos?.speed ?? null,
    fuel: lastPos?.params?.fuel ?? null,
    temperature: 0,
    odometer: odoKm ?? null,
    engineHours: lastPos?.params?.engine_hours ?? null,
    group: device.group || "",
    icon: device.icon || "",
    imei: device.imei || "",
    companyId: device.companyId,
    altitude: lastPos?.altitude ?? 0,
    course: lastPos?.heading ?? 0,
    sensors: {},
    ignition: initialIgnition,
    socketParams: mergeSocketParams({}, lastPos?.params as Record<string, number | string | null> | undefined).params,
    humidity: 0,
    eventCode: lastPos?.event_code ?? 0,
    gps_sat: null,
    engineStatus: engineStatus,
    serialNumber: device.serial || "",
    shipment: "",
    engineCutoff: null,
    hitch: null,
    params: {
      panic: null,
      datetime: lastPos?.timeStamp ? new Date(lastPos.timeStamp).toISOString() : "",
      engineStatus: engineStatus != null ? String(engineStatus) : "",
      imei: device.imei || "",
      latitude: hasLastPosition && lastPos?.lat != null ? lastPos.lat : 0,
    },
    device: {
      type: device.type || "",
      id: device.id,
      mileage: odoKm || null,
      brand_id: device.brand_id || "",
    },
    reference: "",
    h3: {},
    trip: "",
    vehicle_id: device.vehicle_id || "",
    remolque_id: device.remolque_id || "",
    idleSince: initialIdleSince,
  };
};

export const useVehiclesSocketStore = create<VehiclesSocketStore>(
  (set, get) => ({
    vehicles: [],
    loading: true,
    error: null,
    loadError: null,
    reloadRequestId: 0,
    isConnected: false,
    lastReconnect: null,
    isInitialized: false,
    lastInitializedCompanyId: null,
    awaitingCompanySelection: false,
    isLoadingInitialPositions: false,
    initialSyncDone: false,
    pendingDevicesData: [],
    allCompanyDeviceIds: [],
    currentSubscribedDeviceIds: [],
    searchQuery: "",
    debouncedSearchQuery: "",
    selectedStatus: null,
    selectedGroup: "all",
    selectedCompany: localStorage.getItem("companyId"),
    focusedVehicleId: null,
    unitNotificationTick: 0,
    highlightedVehicleId: null,
    loadingAddressDeviceIds: {},
    loadingPositionDeviceIds: {},
    vehiclesMap: new Map(),
    timeTick: 0,

    startTimeTick: () => {
      // Evitar múltiples intervalos
      if (timeTickInterval) return;
      
      timeTickInterval = setInterval(() => {
        set((state) => {
          const now = Date.now();
          let updatedVehicles: VehicleSocket[] | null = null;

          for (let i = 0; i < state.vehicles.length; i++) {
            const vehicle = state.vehicles[i];
            if (vehicle.status === "offline") continue;
            if (
              vehicle.lastReport._seconds > 0 &&
              now - vehicle.lastReport._seconds * 1000 > OFFLINE_THRESHOLD_MS
            ) {
              if (!updatedVehicles) updatedVehicles = [...state.vehicles];
              updatedVehicles[i] = { ...vehicle, status: "offline" };
            } else if (
              vehicle.status === "stopped" &&
              vehicle.idleSince != null &&
              now - vehicle.idleSince >= IDLE_CONFIG.idleThreshold
            ) {
              if (!updatedVehicles) updatedVehicles = [...state.vehicles];
              updatedVehicles[i] = { ...vehicle, status: "ralenti" };
            }
          }

          return updatedVehicles
            ? { timeTick: state.timeTick + 1, vehicles: updatedVehicles }
            : { timeTick: state.timeTick + 1 };
        });
      }, 30000); // 30 segundos
    },

    stopTimeTick: () => {
      if (timeTickInterval) {
        clearInterval(timeTickInterval);
        timeTickInterval = null;
      }
    },

    setSearchQuery: (query) => {
      set({ searchQuery: query });

      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        set({ debouncedSearchQuery: query });
      }, 300);
    },

    clearSearch: () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      set({ searchQuery: "", debouncedSearchQuery: "" });
    },

    setSelectedStatus: (status) => set({ selectedStatus: status }),

    setSelectedGroup: (group) => set({ selectedGroup: group }),

    setSelectedCompany: (company) => {
      set({ selectedCompany: company });
      // Sincronizar con companiesStore (evitar bucle infinito)
      const currentCompanyId = useCompaniesStore.getState().companyId;
      if (currentCompanyId !== company) {
        // Actualizar directamente sin llamar setCompanyId para evitar bucle
        if (company) {
          localStorage.setItem("companyId", company);
        } else {
          localStorage.removeItem("companyId");
        }
        useCompaniesStore.setState({ companyId: company });
      }
    },

    clearFilters: () =>
      set({ selectedStatus: null, selectedGroup: "all", selectedCompany: null }),

    // Sólo se sigue lo que se puede ver: una unidad sin posición dejaba
    // "Siguiendo" y el mapa acercado sobre nada. Aquí y no en cada botón:
    // lo llaman la lista, la tabla, la ficha, viajes y remolques.
    setFocusedVehicleId: (id) => {
      if (id) {
        const idx = get().vehiclesMap.get(id);
        if (idx === undefined || !hasMapPosition(get().vehicles[idx])) return;
      }
      set({ focusedVehicleId: id });
    },

    setHighlightedVehicleId: (id, duration = 5000) => {
      if (highlightTimer) {
        clearTimeout(highlightTimer);
      }

      set({ highlightedVehicleId: id });

      if (id) {
        highlightTimer = setTimeout(() => {
          set({ highlightedVehicleId: null });
        }, duration);
      }
    },

    requestAddressForDevice: (deviceId: string) => {
      set((state) => ({
        loadingAddressDeviceIds: { ...state.loadingAddressDeviceIds, [deviceId]: true },
      }));

      getLastPosition(deviceId, true, deviceId);

      setTimeout(() => {
        const { loadingAddressDeviceIds } = get();
        if (loadingAddressDeviceIds[deviceId]) {
          set((state) => {
            const { [deviceId]: _, ...rest } = state.loadingAddressDeviceIds;
            return { loadingAddressDeviceIds: rest };
          });
        }
      }, 2000);
    },

    initializeWithDevices: (devices: DeviceData[]) => {
      const deviceIds = devices.map(d => d.id);

      const loadingPositionIds: Record<string, boolean> = {};
      const newVehiclesMap = new Map<string, number>();

      const vehiclesFromDevices = devices.map((device, index) => {
        const vehicle = createVehicleFromDevice(device);
        newVehiclesMap.set(device.id, index);

        const hasValidPosition = vehicle.latitude !== 0 || vehicle.longitude !== 0;
        if (!hasValidPosition) {
          loadingPositionIds[device.id] = true;
        }

        return vehicle;
      });

      const devicesWithoutPosition = Object.keys(loadingPositionIds).length;
      const totalDevices = devices.length;

      // Si 80%+ de devices ya tienen posición de la API, no esperar al socket
      const mostDevicesHavePosition = totalDevices > 0 && devicesWithoutPosition / totalDevices < 0.2;

      set({
        vehicles: vehiclesFromDevices,
        vehiclesMap: newVehiclesMap,
        pendingDevicesData: devices,
        isLoadingInitialPositions: !mostDevicesHavePosition,
        initialSyncDone: totalDevices === 0 || mostDevicesHavePosition,
        loading: false,
        loadError: null,
        isInitialized: true,
        awaitingCompanySelection: false,
        allCompanyDeviceIds: deviceIds,
        loadingPositionDeviceIds: mostDevicesHavePosition ? {} : loadingPositionIds,
      });
    },

    finalizeInitialLoad: (positions: Map<string, PositionFromSocket>) => {
      const { vehicles, vehiclesMap, loadingPositionDeviceIds } = get();

      if (positions.size === 0) {
        // Si no hay posiciones nuevas, solo limpiar estado de loading
        set({
          isLoadingInitialPositions: false,
          initialSyncDone: true,
          pendingDevicesData: [],
          loadingPositionDeviceIds: {},
        });
        return;
      }

      // Actualizar vehículos existentes con las posiciones recibidas
      const updatedVehicles = [...vehicles];
      const updatedLoadingIds = { ...loadingPositionDeviceIds };
      let updatedCount = 0;

      positions.forEach((socketPosition, deviceId) => {
        // Usar Map para lookup O(1)
        const vehicleIndex = vehiclesMap.get(deviceId);
        if (vehicleIndex === undefined) return;

        const existingVehicle = updatedVehicles[vehicleIndex];
        if (!existingVehicle) return;

        if (socketPosition.lat != null && socketPosition.long != null) {
          const mergedParams = mergeSocketParams(existingVehicle.socketParams, socketPosition.rawParams).params;
          const metrics = resolveVehicleMetrics(socketPosition, mergedParams, existingVehicle);

          const effectiveSpeed = resolveEffectiveSpeed(
            socketPosition.speed,
            existingVehicle.odometer,
            metrics.odometer,
            existingVehicle.latitude,
            existingVehicle.longitude,
            socketPosition.lat,
            socketPosition.long,
            existingVehicle.lastReport._seconds,
            socketPosition.timeStamp
          );

          const { status: newStatus, idleSince: newIdleSince } =
            determineVehicleStatus(
              effectiveSpeed,
              metrics.ignitionState,
              socketPosition.timeStamp,
              existingVehicle.status,
              undefined,
              existingVehicle.idleSince,
              metrics.idle
            );

          updatedVehicles[vehicleIndex] = {
            ...existingVehicle,
            status: newStatus,
            idleSince: newIdleSince,
            coordinates: `${socketPosition.lat},${socketPosition.long}`,
            latitude: socketPosition.lat,
            longitude: socketPosition.long,
            battery: metrics.battery,
            signal: socketPosition.params?.rssi ?? existingVehicle.signal,
            lastReport: {
              _seconds: timestampToSeconds(socketPosition.timeStamp),
              _nanoseconds: 0,
            },
            // Respetar null explícito del servidor (importante para lógica de motor)
            speed: socketPosition.speed !== undefined ? socketPosition.speed : existingVehicle.speed,
            fuel: socketPosition.params?.fuel ?? existingVehicle.fuel,
            temperature: socketPosition.params?.dallas_temperature ?? existingVehicle.temperature,
            odometer: metrics.odometer,
            engineHours: socketPosition.params?.engine_hours ?? existingVehicle.engineHours,
            altitude: socketPosition.altitude ?? existingVehicle.altitude,
            // Respetar siempre el último heading reportado por el device:
            // si llega 0 (Norte) o null se actualiza igual, sin preservar
            // el valor anterior. El MapView ya se encarga de mostrar un
            // círculo en vez de flecha cuando course === 0/null.
            course: socketPosition.heading ?? 0,
            ignition: metrics.ignition,
            eventCode: socketPosition.event_code ?? existingVehicle.eventCode,
            gps_sat: metrics.gpsSat,
            engineStatus: metrics.statusEngineStatus ?? existingVehicle.engineStatus,
            params: {
              ...existingVehicle.params,
              // Ver comentario en updateVehiclePosition: respetamos el panic
              // que vino con esta posición tal cual (puede ser null para
              // limpiar el SOS cuando llega un retorno de alerta).
              panic: metrics.panic,
              datetime: safeToISOString(socketPosition.timeStamp),
              engineStatus: socketPosition.params?.engine_status != null ? String(socketPosition.params.engine_status) : existingVehicle.params.engineStatus,
              latitude: socketPosition.lat,
            },
            device: {
              ...existingVehicle.device,
              mileage: socketPosition.params?.odo_km ?? existingVehicle.device.mileage,
            },
            vehicle_id: socketPosition.vehicle_id || existingVehicle.vehicle_id,
            remolque_id: socketPosition.remolque_id || existingVehicle.remolque_id,
            socketParams: mergedParams,
            companyId: socketPosition.company_id || existingVehicle.companyId,
            brandModel: metrics.brandModel,
            brandName: metrics.brandName,
            engineCutoff: metrics.engineCutoff,
            hitch: metrics.hitch,
          };

          // Remover del loading ya que recibió posición
          delete updatedLoadingIds[deviceId];
          updatedCount++;
        }
      });
      set({
        vehicles: updatedVehicles,
        pendingDevicesData: [],
        isLoadingInitialPositions: false,
        initialSyncDone: true,
        loadingPositionDeviceIds: {}, // Clear ALL loading states - initial load is complete
      });
    },

    connect: (deviceIds: string[]) => {
      const store = get();

      // Iniciar el tick global para actualizar tiempos
      store.startTimeTick();

      // Guardar los device IDs actuales
      set({ currentSubscribedDeviceIds: deviceIds });

      // Limpiar intervalo anterior si existe
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
      }

      try {
        // Conectar al socket
        const socket = connectSocket();

        // Remover listeners anteriores para evitar duplicados
        socket.removeAllListeners();

        const subscribeAndFetchPositions = () => {
          const currentDeviceIds = get().currentSubscribedDeviceIds;
          const { isLoadingInitialPositions } = get();
          subscribeToDevices(currentDeviceIds);

          if (isLoadingInitialPositions) {
            isInitialLoadPhase = true;
            initialLoadPositions = new Map();
          }

          if (currentDeviceIds.length > 0) {
            setTimeout(() => {
              getLastPositionBatch(currentDeviceIds, false);

              // Fetch current trip states for all subscribed devices
              currentDeviceIds.forEach((id) => getTripStatus(id));

              setTimeout(() => {
                if (isInitialLoadPhase) {
                  store.finalizeInitialLoad(initialLoadPositions);
                  isInitialLoadPhase = false;
                  initialLoadPositions = new Map();
                }
              }, INITIAL_LOAD_TIMEOUT);
            }, 100);
          } else if (isInitialLoadPhase) {
            store.finalizeInitialLoad(new Map());
            isInitialLoadPhase = false;
          }
        };

        // Si ya está conectado, suscribirse inmediatamente
        if (socket.connected) {
          set({ isConnected: true, error: null, lastReconnect: new Date() });
          subscribeAndFetchPositions();
        }

        // Listener de conexión (para reconexiones o primera conexión)
        socket.on("connect", () => {
          socketConnectErrorCount = 0;
          serverDisconnectCount = 0;
          set((state) => ({
            isConnected: true,
            error: null,
            lastReconnect: new Date(),
            loadError:
              state.loadError === "SOCKET_GAVE_UP" ? null : state.loadError,
          }));
          subscribeAndFetchPositions();
        });

        // Listener de desconexión
        socket.on("disconnect", (reason) => {
          console.log(`[Socket] Disconnected. Reason: ${reason}`);
          set({ isConnected: false });

          if (reason !== "io server disconnect") return;
          serverDisconnectCount += 1;
          if (serverDisconnectCount > MAX_SERVER_DISCONNECT_RETRIES) {
            trackDiagnostic("socket_gave_up", {
              reason,
              server_disconnects: serverDisconnectCount,
            });
            set({ loadError: "SOCKET_GAVE_UP" });
            return;
          }
          trackDiagnostic("socket_server_disconnect", {
            server_disconnects: serverDisconnectCount,
          });
          const delay = Math.min(2000 * 2 ** (serverDisconnectCount - 1), 60000);
          setTimeout(() => {
            if (get().currentSubscribedDeviceIds.length > 0) connectSocket();
          }, delay);
        });

        if (reconnectFailedHandler) {
          socket.io.off("reconnect_failed", reconnectFailedHandler);
        }
        reconnectFailedHandler = () => {
          trackDiagnostic("socket_gave_up", {
            reason: "reconnect_failed",
            attempts: SOCKET_MAX_RECONNECT_ATTEMPTS,
            last_error: get().error,
            device_count: get().currentSubscribedDeviceIds.length,
          });
          set({ isConnected: false, loadError: "SOCKET_GAVE_UP" });
        };
        socket.io.on("reconnect_failed", reconnectFailedHandler);

        // Listener de error de conexión
        socket.on("connect_error", (error) => {
          socketConnectErrorCount += 1;
          set({ error: error.message, isConnected: false });
          if (socketConnectErrorCount === 1) {
            trackDiagnostic("socket_connect_error", {
              socket_error: error.message,
              device_count: get().currentSubscribedDeviceIds.length,
            });
          }
        });

        // Listener de suscripción exitosa
        // socket.on("subscribed", (response) => {
        //   console.log("subscribed:", response);
        // });

        // Listener de posiciones
        socket.on("position", (position: any) => {
          const positionData = {
            device_id: position.device_id,
            company_id: position.company_id,
            vehicle_id: position.vehicle_id ?? "",
            remolque_id: position.remolque_id ?? "",
            imei: position?.imei ?? null,
            brand_model: position?.brand_model ?? position?.point?.brand_model ?? null,
            brand_name: position?.brand_name ?? position?.point?.brand_name ?? null,
            timeStamp: position?.point?.timeStamp,
            lat: position?.point?.lat ?? null,
            long: position?.point?.lon ?? null,
            heading: position?.point?.heading ?? null,
            battery: position?.point?.battery ?? null,
            altitude: position?.point?.altitude ?? null,
            accuracy: position?.point?.accuracy ?? null,
            event_code: position?.point?.event_code ?? null,
            speed: position?.point?.speed ?? null,
            ignition: position?.point?.ignition ?? null,
            params: position?.point?.params ?? null,
            rawParams: position?.point?.params ?? null,
          }

          store.updateVehiclePosition(positionData);
        });

        // Listener de última posición
        socket.on("last_position", (position: any) => {
          const point = position?.point;
          // El deviceId puede venir en position.device_id o en point.device_id
          const deviceId = position?.device_id ?? point?.device_id ?? null;

          if (!deviceId) return;

          // Verificar si es una solicitud de address individual (no de batch)
          const { loadingAddressDeviceIds } = get();
          const isAddressRequest = !!loadingAddressDeviceIds[deviceId];

          // Remover de loading de address si aplica (O(1))
          if (isAddressRequest) {
            set((state) => {
              const { [deviceId]: _, ...rest } = state.loadingAddressDeviceIds;
              return { loadingAddressDeviceIds: rest };
            });
          }

          if (!point || (point.lat == null && point.lon == null)) {
            return;
          }

          // Si tiene address, actualizar solo el address (siempre, incluso en carga inicial)
          if (point?.address?.label && !isInitialLoadPhase) {
            store.updateParameters({
              address: point.address.label,
              device_id: deviceId
            });
          }

          // Construir datos de posición
          if (point?.lat != null && point?.lon != null) {
            const positionData: PositionFromSocket = {
              device_id: deviceId,
              company_id: point?.company_id ?? "",
              vehicle_id: point?.vehicle_id ?? "",
              remolque_id: point?.remolque_id ?? "",
              imei: point?.imei ?? "",
              brand_model: position?.brand_model ?? point?.brand_model ?? null,
              brand_name: position?.brand_name ?? point?.brand_name ?? null,
              timeStamp: point?.timeStamp ?? Date.now(),
              rawParams: point?.params ?? null,
              lat: point.lat,
              long: point.lon,
              heading: point?.heading ?? 0,
              battery: point?.battery ?? null,
              altitude: point?.altitude ?? 0,
              event_code: point?.event_code ?? 0,
              speed: point?.speed ?? null,
              ignition: point?.ignition ?? null,
              params: {
                panic: point?.params?.panic ?? null,
                engine_status: point?.params?.engine_status ?? null,
                event_code: point?.params?.event_code ?? 0,
                gps_sat: point?.params?.gps_sat ?? undefined,
                odo_km: point?.params?.odo_km ?? null,
                fuel: point?.params?.fuel ?? null,
                engine_hours: point?.params?.engine_hours ?? null,
                provider: point?.params?.provider ?? "",
                rssi: point?.params?.rssi ?? undefined,
                sim: point?.params?.sim ?? undefined,
                dallas_temperature: point?.params?.dallas_temperature ?? undefined,
              },
            };

            // Si estamos en fase de carga inicial, acumular en el mapa
            if (isInitialLoadPhase) {
              initialLoadPositions.set(deviceId, positionData);
              const expectedCount = get().currentSubscribedDeviceIds.length;
              if (expectedCount > 0 && initialLoadPositions.size >= expectedCount) {
                const completeBatch = initialLoadPositions;
                isInitialLoadPhase = false;
                initialLoadPositions = new Map();
                store.finalizeInitialLoad(completeBatch);
              }
            } else {
              // Si no es carga inicial, actualizar normalmente
              store.updateVehiclePosition(positionData);
            }
          }
        });

        // Listener de eventos (también actualiza posiciones)
        socket.on("event", (eventData: any) => {
          const event = eventData?.event;
          // Construir objeto de evento con toda la información disponible
          const updatedEvent = {
            // Identificadores
            device_id: eventData?.device_id ?? event?.device_id ?? null,
            company_id: eventData?.company_id ?? null,
            vehicle_id: eventData?.vehicle_id,
            remolque_id: eventData?.remolque_id,

            // Datos del evento
            type: event?.metadata?.origin ?? null,
            event_type: event?.event_type ?? null,
            event_code: event?.event_code ?? null,

            // Posición del evento
            lat: event?.lat ?? null,
            lon: event?.lon ?? null,
            timeStamp: event?.timeStamp ?? null,

            // Parámetros
            gps_sat: event?.params?.gps_sat ?? null,
            engine_status: event?.params?.engine_status ?? null,
            odo_km: event?.params?.odo_km ?? null,
            rssi: event?.params?.rssi ?? null,
            sim: event?.params?.sim ?? null,
            panic: event?.params?.panic ?? null,
            params: event?.params ?? null,
          };

          // Actualizar estado del vehículo
          store.updateEvent(updatedEvent);

          // Agregar evento al store de eventos por device
          if (updatedEvent.device_id) {
            useDeviceEventsStore.getState().addEvent(updatedEvent);
          }
        });

        // Listener de command_status (actualizaciones de estado de comandos)
        socket.on("command_status", (commandData: any) => {
          if (commandData?.device_id && commandData?.command) {
            useDeviceCommandsStore.getState().addCommand(commandData);
          }
        });

        // Listener de trip_tracking (actualizaciones de viajes en tiempo real)
        socket.on("trip_tracking", (trackingData: any) => {
          if (trackingData?.device_id) {
            useTripTrackingStore.getState().updateFromEvent(trackingData);
          }
        });

        // Listener de trip_status (respuesta a get:trip_status)
        socket.on("notification", () => {
          set((state) => ({ unitNotificationTick: state.unitNotificationTick + 1 }));
        });

        socket.on("trip_status", (statusData: any) => {
          if (statusData?.deviceId) {
            useTripTrackingStore.getState().updateFromTripStatus(statusData);
          }
        });

        // Configurar reconexión automática cada 55 minutos
        reconnectInterval = setInterval(() => {
          disconnectSocket();
          setTimeout(() => {
            connectSocket();
          }, 1000);
        }, RECONNECT_INTERVAL);

      } catch (error: any) {
        console.error("Error conectando socket:", error);
        set({ error: error.message, loading: false });
      }
    },

    disconnect: () => {
      const store = get();

      // Detener el tick global de tiempos
      store.stopTimeTick();

      // Limpiar intervalo de reconexión
      if (reconnectInterval) {
        clearInterval(reconnectInterval);
        reconnectInterval = null;
      }

      // Remover listeners
      const socket = getSocket();
      socket.removeAllListeners();
      if (reconnectFailedHandler) {
        socket.io.off("reconnect_failed", reconnectFailedHandler);
        reconnectFailedHandler = null;
      }
      socketConnectErrorCount = 0;
      serverDisconnectCount = 0;

      // Desconectar
      disconnectSocket();
      set({ isConnected: false, currentSubscribedDeviceIds: [] });
    },

    resubscribe: (deviceIds: string[]) => {
      const { isConnected } = get();

      // Actualizar los device IDs actuales
      set({ currentSubscribedDeviceIds: deviceIds });

      // Si estamos conectados, re-suscribirse
      if (isConnected) {
        subscribeToDevices(deviceIds);
      } else {
        console.log("⚠️ [Socket] No conectado, la suscripción se hará al reconectar");
      }
    },

    updateVehiclePosition: (position: PositionFromSocket) => {
      set((state) => {
        // Usar Map para lookup O(1)
        let vehicleIndex = state.vehiclesMap.get(position.device_id);

        // Remover del loading de posición si estaba esperando
        let updatedLoadingIds = state.loadingPositionDeviceIds;
        if (state.loadingPositionDeviceIds[position.device_id]) {
          const { [position.device_id]: _, ...rest } = state.loadingPositionDeviceIds;
          updatedLoadingIds = rest;
        }

        if (vehicleIndex === undefined) {
          const newSocketParams = mergeSocketParams({}, position.rawParams).params;
          const metrics = resolveVehicleMetrics(position, newSocketParams);
          const initial = determineVehicleStatus(
            position.speed,
            metrics.ignitionState,
            position.timeStamp,
            "stopped",
            undefined,
            undefined,
            metrics.idle
          );
          const newVehicle: VehicleSocket = {
            id: position.device_id,
            fullId: position.device_id,
            status: initial.status,
            driver: {
              name: "Sin asignar",
              licenseId: "",
              photo: "",
              experience: "",
              phone: "",
            },
            vehicle: {
              brand: "",
              model: "",
              year: 0,
              plates: "",
              color: "",
              name: position.device_id,
              type: position.remolque_id ? "remolque" : position.vehicle_id ? "vehicle" : "",
            },
            address: "",
            coordinates: `${position.lat},${position.long}`,
            latitude: position.lat,
            longitude: position.long,
            battery: position.battery ?? null,
            signal: position.params?.rssi || 0,
            lastReport: {
              _seconds: timestampToSeconds(position.timeStamp),
              _nanoseconds: 0,
            },
            speed: position.speed ?? null,
            fuel: position.params?.fuel ?? null,
            temperature: position.params?.dallas_temperature || 0,
            odometer: position.params?.odo_km ?? null,
            engineHours: position.params?.engine_hours ?? null,
            group: "",
            icon: "",
            imei: position.imei,
            companyId: position.company_id,
            altitude: position.altitude || 0,
            course: position.heading || 0,
            sensors: {},
            ignition: position.params?.engine_status === 1 || position.params?.engine_status === 2 || position.params?.engine_status === 3,
            humidity: 0,
            eventCode: position.event_code || 0,
            gps_sat: position.params?.gps_sat ?? null,
            engineStatus: position.params?.engine_status ?? null,
            serialNumber: "",
            shipment: "",
            engineCutoff: metrics.engineCutoff,
            hitch: metrics.hitch,
            params: {
              panic: derivePanic(position.params?.panic, position.event_code),
              datetime: safeToISOString(position.timeStamp),
              engineStatus: position.params?.engine_status != null ? String(position.params.engine_status) : "",
              imei: position.imei,
              latitude: position.lat,
            },
            device: {
              type: "",
              id: position.device_id,
              mileage: position.params?.odo_km ?? null,
            },
            reference: "",
            h3: {},
            trip: "",
            vehicle_id: position.vehicle_id,
            remolque_id: position.remolque_id,
          };

          newVehicle.socketParams = newSocketParams;
          newVehicle.battery = metrics.battery;
          newVehicle.gps_sat = metrics.gpsSat;
          newVehicle.odometer = metrics.odometer;
          newVehicle.ignition = metrics.ignition;
          newVehicle.engineStatus = metrics.statusEngineStatus ?? newVehicle.engineStatus;
          newVehicle.params.panic = metrics.panic;
          newVehicle.brandModel = metrics.brandModel;
          newVehicle.brandName = metrics.brandName;
          newVehicle.idleSince = initial.idleSince;
          const newMap = new Map(state.vehiclesMap);
          newMap.set(position.device_id, state.vehicles.length);

          return {
            vehicles: [...state.vehicles, newVehicle],
            vehiclesMap: newMap,
            loadingPositionDeviceIds: updatedLoadingIds,
          };
        }

        // Actualizar vehículo existente
        const existingVehicle = state.vehicles[vehicleIndex];

        const paramsResult = mergeSocketParams(existingVehicle.socketParams, position.rawParams);
        const metrics = resolveVehicleMetrics(position, paramsResult.params, existingVehicle);

        const effectiveSpeed = resolveEffectiveSpeed(
          position.speed,
          existingVehicle.odometer,
          metrics.odometer,
          existingVehicle.latitude,
          existingVehicle.longitude,
          position.lat,
          position.long,
          existingVehicle.lastReport._seconds,
          position.timeStamp
        );

        const { status: newStatus, idleSince: newIdleSince } =
          determineVehicleStatus(
            effectiveSpeed,
            metrics.ignitionState,
            position.timeStamp,
            existingVehicle.status,
            undefined,
            existingVehicle.idleSince,
            metrics.idle
          );

        const newLat = position.lat;
        const newLng = position.long;
        // Respetar null explícito del servidor (importante para lógica de motor)
        const newSpeed = position.speed !== undefined ? position.speed : existingVehicle.speed;
        const newTimestamp = timestampToSeconds(position.timeStamp);

        const hasPositionChanged =
          existingVehicle.latitude !== newLat ||
          existingVehicle.longitude !== newLng;
        const hasStatusChanged = existingVehicle.status !== newStatus;

        const newBattery = metrics.battery;
        const newEngineStatus = metrics.statusEngineStatus ?? existingVehicle.engineStatus;
        const newOdometer = metrics.odometer;
        const newGpsSat = metrics.gpsSat;
        const newEventCode = position.event_code ?? existingVehicle.eventCode;

        const hasCardDataChanged =
          existingVehicle.lastReport._seconds !== newTimestamp ||
          existingVehicle.speed !== newSpeed ||
          existingVehicle.engineStatus !== newEngineStatus ||
          existingVehicle.battery !== newBattery ||
          existingVehicle.gps_sat !== newGpsSat ||
          existingVehicle.odometer !== newOdometer ||
          existingVehicle.eventCode !== newEventCode ||
          existingVehicle.engineCutoff !== metrics.engineCutoff ||
          existingVehicle.hitch !== metrics.hitch ||
          existingVehicle.idleSince !== newIdleSince ||
          paramsResult.changed;

        if (!hasPositionChanged && !hasStatusChanged && !hasCardDataChanged) {
          if (updatedLoadingIds !== state.loadingPositionDeviceIds) {
            return { loadingPositionDeviceIds: updatedLoadingIds };
          }
          return { vehicles: state.vehicles };
        }

        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...existingVehicle,
          status: newStatus,
          idleSince: newIdleSince,
          coordinates: `${newLat},${newLng}`,
          latitude: newLat,
          longitude: newLng,
          battery: newBattery,
          signal: position.params?.rssi ?? existingVehicle.signal,
          lastReport: {
            _seconds: newTimestamp,
            _nanoseconds: 0,
          },
          speed: newSpeed,
          fuel: position.params?.fuel ?? existingVehicle.fuel,
          temperature: position.params?.dallas_temperature ?? existingVehicle.temperature,
          odometer: newOdometer,
          engineHours: position.params?.engine_hours ?? existingVehicle.engineHours,
          altitude: position.altitude ?? existingVehicle.altitude,
          // Ver comentario en finalizeInitialLoad: el course refleja siempre
          // el último heading reportado por el device, sin preservar el
          // anterior cuando llega 0/null.
          course: position.heading ?? 0,
          ignition: metrics.ignition,
          eventCode: newEventCode,
          gps_sat: newGpsSat,
          engineStatus: newEngineStatus,
          params: {
            ...existingVehicle.params,
            panic: metrics.panic,
            datetime: safeToISOString(position.timeStamp),
            engineStatus: position.params?.engine_status != null ? String(position.params.engine_status) : existingVehicle.params.engineStatus,
            latitude: newLat,
          },
          device: {
            ...existingVehicle.device,
            mileage: position.params?.odo_km ?? existingVehicle.device.mileage,
          },
          vehicle_id: position.vehicle_id,
          remolque_id: position.remolque_id,
          companyId: position.company_id || existingVehicle.companyId,
          socketParams: paramsResult.params,
          brandModel: metrics.brandModel,
          brandName: metrics.brandName,
          engineCutoff: metrics.engineCutoff,
          hitch: metrics.hitch,
        };

        return {
          vehicles: updatedVehicles,
          loadingPositionDeviceIds: updatedLoadingIds,
        };
      });
    },

    // Batch update para múltiples posiciones (1 solo re-render)
    updateVehiclePositionsBatch: (positions: PositionFromSocket[]) => {
      if (positions.length === 0) return;

      set((state) => {
        let vehicles = [...state.vehicles];
        let hasChanges = false;
        let newVehiclesMap = state.vehiclesMap;
        let updatedLoadingIds = { ...state.loadingPositionDeviceIds };
        let loadingIdsChanged = false;

        for (const position of positions) {
          // Usar Map para lookup O(1)
          let vehicleIndex = state.vehiclesMap.get(position.device_id);

          // Remover del loading si estaba esperando
          if (updatedLoadingIds[position.device_id]) {
            delete updatedLoadingIds[position.device_id];
            loadingIdsChanged = true;
          }

          if (vehicleIndex === undefined) {
            // Crear nuevo vehículo
            const newSocketParams = mergeSocketParams({}, position.rawParams).params;
            const metrics = resolveVehicleMetrics(position, newSocketParams);
            const initial = determineVehicleStatus(
              position.speed,
              metrics.ignitionState,
              position.timeStamp,
              "stopped",
              undefined,
              undefined,
              metrics.idle
            );
            const newVehicle: VehicleSocket = {
              id: position.device_id,
              fullId: position.device_id,
              status: initial.status,
              driver: {
                name: "Sin asignar",
                licenseId: "",
                photo: "",
                experience: "",
                phone: "",
              },
              vehicle: {
                brand: "",
                model: "",
                year: 0,
                plates: "",
                color: "",
                name: position.device_id,
                type: position.remolque_id ? "remolque" : position.vehicle_id ? "vehicle" : "",
              },
              address: "",
              coordinates: `${position.lat},${position.long}`,
              latitude: position.lat,
              longitude: position.long,
              battery: position.battery ?? null,
              signal: position.params?.rssi || 0,
              lastReport: {
                _seconds: timestampToSeconds(position.timeStamp),
                _nanoseconds: 0,
              },
              speed: position.speed ?? null,
              fuel: position.params?.fuel ?? null,
              temperature: position.params?.dallas_temperature || 0,
              odometer: position.params?.odo_km ?? null,
              engineHours: position.params?.engine_hours ?? null,
              group: "",
              icon: "",
              imei: position.imei,
              companyId: position.company_id,
              altitude: position.altitude || 0,
              course: position.heading || 0,
              sensors: {},
              ignition: position.params?.engine_status === 1 || position.params?.engine_status === 2 || position.params?.engine_status === 3,
              humidity: 0,
              eventCode: position.event_code || 0,
              gps_sat: position.params?.gps_sat ?? null,
              engineStatus: position.params?.engine_status ?? null,
              serialNumber: "",
              shipment: "",
              engineCutoff: metrics.engineCutoff,
              hitch: metrics.hitch,
              params: {
                panic: derivePanic(position.params?.panic, position.event_code),
                datetime: safeToISOString(position.timeStamp),
                engineStatus: position.params?.engine_status != null ? String(position.params.engine_status) : "",
                imei: position.imei,
                latitude: position.lat,
              },
              device: {
                type: "",
                id: position.device_id,
                mileage: position.params?.odo_km ?? null,
              },
              reference: "",
              h3: {},
              trip: "",
              vehicle_id: position.vehicle_id,
              remolque_id: position.remolque_id,
            };

            // Actualizar Map con el nuevo índice
            if (newVehiclesMap === state.vehiclesMap) {
              newVehiclesMap = new Map(state.vehiclesMap);
            }
            newVehiclesMap.set(position.device_id, vehicles.length);

            newVehicle.socketParams = newSocketParams;
            newVehicle.battery = metrics.battery;
            newVehicle.gps_sat = metrics.gpsSat;
            newVehicle.odometer = metrics.odometer;
            newVehicle.ignition = metrics.ignition;
            newVehicle.engineStatus = metrics.statusEngineStatus ?? newVehicle.engineStatus;
            newVehicle.params.panic = metrics.panic;
            newVehicle.brandModel = metrics.brandModel;
            newVehicle.brandName = metrics.brandName;
            newVehicle.idleSince = initial.idleSince;
            vehicles = [...vehicles, newVehicle];
            hasChanges = true;
            continue;
          }

          // Actualizar vehículo existente
          const existingVehicle = vehicles[vehicleIndex];

          const batchParamsResult = mergeSocketParams(existingVehicle.socketParams, position.rawParams);
          const metrics = resolveVehicleMetrics(position, batchParamsResult.params, existingVehicle);

          const effectiveSpeed = resolveEffectiveSpeed(
            position.speed,
            existingVehicle.odometer,
            metrics.odometer,
            existingVehicle.latitude,
            existingVehicle.longitude,
            position.lat,
            position.long,
            existingVehicle.lastReport._seconds,
            position.timeStamp
          );

          const { status: newStatus, idleSince: newIdleSince } =
            determineVehicleStatus(
              effectiveSpeed,
              metrics.ignitionState,
              position.timeStamp,
              existingVehicle.status,
              undefined,
              existingVehicle.idleSince,
              metrics.idle
            );

          const newLat = position.lat;
          const newLng = position.long;
          // Respetar null explícito del servidor (importante para lógica de motor)
          const newSpeed = position.speed !== undefined ? position.speed : existingVehicle.speed;
          const newTimestamp = timestampToSeconds(position.timeStamp);

          // Verificar si los datos críticos cambiaron
          const hasPositionChanged =
            existingVehicle.latitude !== newLat ||
            existingVehicle.longitude !== newLng;
          const hasStatusChanged = existingVehicle.status !== newStatus;

          const newBatchBattery = metrics.battery;
          const newBatchEngineStatus = metrics.statusEngineStatus ?? existingVehicle.engineStatus;
          const newBatchOdometer = metrics.odometer;
          const newBatchGpsSat = metrics.gpsSat;
          const newBatchEventCode = position.event_code ?? existingVehicle.eventCode;

          const hasBatchCardDataChanged =
            existingVehicle.lastReport._seconds !== newTimestamp ||
            existingVehicle.speed !== newSpeed ||
            existingVehicle.engineStatus !== newBatchEngineStatus ||
            existingVehicle.battery !== newBatchBattery ||
            existingVehicle.gps_sat !== newBatchGpsSat ||
            existingVehicle.odometer !== newBatchOdometer ||
            existingVehicle.eventCode !== newBatchEventCode ||
            existingVehicle.engineCutoff !== metrics.engineCutoff ||
            existingVehicle.hitch !== metrics.hitch ||
            existingVehicle.idleSince !== newIdleSince ||
            batchParamsResult.changed;

          if (!hasPositionChanged && !hasStatusChanged && !hasBatchCardDataChanged) {
            continue;
          }

          vehicles[vehicleIndex] = {
            ...existingVehicle,
            status: newStatus,
            idleSince: newIdleSince,
            coordinates: `${newLat},${newLng}`,
            latitude: newLat,
            longitude: newLng,
            battery: newBatchBattery,
            signal: position.params?.rssi ?? existingVehicle.signal,
            lastReport: {
              _seconds: newTimestamp,
              _nanoseconds: 0,
            },
            speed: newSpeed,
            fuel: position.params?.fuel ?? existingVehicle.fuel,
            temperature: position.params?.dallas_temperature ?? existingVehicle.temperature,
            odometer: newBatchOdometer,
            engineHours: position.params?.engine_hours ?? existingVehicle.engineHours,
            altitude: position.altitude ?? existingVehicle.altitude,
            // Ver comentario en finalizeInitialLoad: el course refleja
            // siempre el último heading reportado, sin preservar el
            // anterior.
            course: position.heading ?? 0,
            ignition: metrics.ignition,
            eventCode: newBatchEventCode,
            gps_sat: newBatchGpsSat,
            engineStatus: newBatchEngineStatus,
            params: {
              ...existingVehicle.params,
              panic: metrics.panic,
              datetime: safeToISOString(position.timeStamp),
              engineStatus: position.params?.engine_status != null ? String(position.params.engine_status) : existingVehicle.params.engineStatus,
              latitude: newLat,
            },
            device: {
              ...existingVehicle.device,
              mileage: position.params?.odo_km ?? existingVehicle.device.mileage,
            },
            vehicle_id: position.vehicle_id,
            remolque_id: position.remolque_id,
            companyId: position.company_id || existingVehicle.companyId,
            socketParams: batchParamsResult.params,
            brandModel: metrics.brandModel,
            brandName: metrics.brandName,
            engineCutoff: metrics.engineCutoff,
            hitch: metrics.hitch,
          };
          hasChanges = true;
        }

        // Construir objeto de retorno con solo los campos que cambiaron
        const result: any = {};
        if (hasChanges) {
          result.vehicles = vehicles;
        }
        if (newVehiclesMap !== state.vehiclesMap) {
          result.vehiclesMap = newVehiclesMap;
        }
        if (loadingIdsChanged) {
          result.loadingPositionDeviceIds = updatedLoadingIds;
        }

        return Object.keys(result).length > 0 ? result : { vehicles: state.vehicles };
      });
    },

    updateParameters: (parameters: any) => {
      set((state) => {
        // Usar Map para lookup O(1)
        const vehicleIndex = state.vehiclesMap.get(parameters.device_id);

        if (vehicleIndex === undefined) {
          return { vehicles: state.vehicles };
        }

        const existingVehicle = state.vehicles[vehicleIndex];

        // Si el address no cambió, no actualizar
        if (existingVehicle.address === parameters.address) {
          return { vehicles: state.vehicles };
        }

        // Crear nuevo array con nuevo objeto para que Zustand detecte el cambio
        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...existingVehicle,
          address: parameters.address,
        };

        return { vehicles: updatedVehicles };
      });
    },

    updateDriver: (device_id: string, driver: VehicleSocket["driver"]) => {
      set((state) => {
        const vehicleIndex = state.vehiclesMap.get(device_id);
        if (vehicleIndex === undefined) {
          return { vehicles: state.vehicles };
        }
        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...updatedVehicles[vehicleIndex],
          driver,
        };
        return { vehicles: updatedVehicles };
      });
    },
    updateVehicle: (device_id: string, vehicle: VehicleSocket["vehicle"]) => {
      set((state) => {
        const vehicleIndex = state.vehiclesMap.get(device_id);
        if (vehicleIndex === undefined) {
          return { vehicles: state.vehicles };
        }
        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...updatedVehicles[vehicleIndex],
          vehicle,
        };
        return { vehicles: updatedVehicles };
      });
    },
    updateRemolque: (device_id: string, remolque_id: string | null) => {
      set((state) => {
        const vehicleIndex = state.vehiclesMap.get(device_id);
        if (vehicleIndex === undefined) {
          return { vehicles: state.vehicles };
        }
        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...updatedVehicles[vehicleIndex],
          remolque_id: remolque_id || "",
        };
        return { vehicles: updatedVehicles };
      });
    },
    refreshDevicesData: (devices: DeviceData[]) => {
      const state = get();
      const updatedVehicles = [...state.vehicles];
      const updatedMap = new Map(state.vehiclesMap);
      const newDeviceIds: string[] = [];

      for (const device of devices) {
        const existingIndex = updatedMap.get(device.id);

        if (existingIndex !== undefined) {
          const existing = updatedVehicles[existingIndex];
          const vehiclePlates = device.vehicle?.plates || device.vehicle?.plate || "";

          // Determinar tipo de unidad por endpoint: remolque_id → trailer, vehicle_id → vehículo
          const refreshUnitType = device.remolque_id ? "remolque" : device.vehicle_id ? "vehicle" : existing.vehicle.type;

          updatedVehicles[existingIndex] = {
            ...existing,
            fullId: device.fullId || device.identifier || existing.fullId,
            vehicle: device.vehicle
              ? {
                  brand: device.vehicle.brand || "",
                  model: device.vehicle.model || device.model || "",
                  year: device.vehicle.year || 0,
                  plates: vehiclePlates,
                  color: device.vehicle.color || "",
                  name: device.vehicle.name || existing.vehicle.name,
                  type: refreshUnitType,
                  bodyType: device.vehicle.type || existing.vehicle.bodyType || "",
                  pointerModel: device.vehicle.pointer_model ?? existing.vehicle.pointerModel ?? "",
                }
              : existing.vehicle,
            driver: device.driver
              ? {
                  id: device.driver.id,
                  name: device.driver.name || "Sin asignar",
                  licenseId: device.driver.licenseId || "",
                  photo: device.driver.photo || "",
                  experience: device.driver.experience || "",
                  phone: device.driver.phone || "",
                  email: device.driver.email,
                }
              : existing.driver,
            group: device.group || existing.group,
            icon: device.icon || existing.icon,
            imei: device.imei || existing.imei,
            serialNumber: device.serial || existing.serialNumber,
            vehicle_id: device.vehicle_id || "",
            remolque_id: device.remolque_id || "",
            companyId: device.companyId || existing.companyId,
          };
        } else {
          const newVehicle = createVehicleFromDevice(device);
          const newIndex = updatedVehicles.length;
          updatedVehicles.push(newVehicle);
          updatedMap.set(device.id, newIndex);
          newDeviceIds.push(device.id);
        }
      }

      const allDeviceIds = devices.map((d) => d.id);

      const incomingIds = new Set(allDeviceIds);
      const removedIds =
        devices.length > 0
          ? state.vehicles.filter((v) => !incomingIds.has(v.id)).map((v) => v.id)
          : [];

      let finalVehicles = updatedVehicles;
      let finalMap = updatedMap;
      if (removedIds.length > 0) {
        const removedSet = new Set(removedIds);
        finalVehicles = updatedVehicles.filter((v) => !removedSet.has(v.id));
        finalMap = new Map();
        finalVehicles.forEach((v, idx) => finalMap.set(v.id, idx));
      }

      set({
        vehicles: finalVehicles,
        vehiclesMap: finalMap,
        allCompanyDeviceIds: allDeviceIds,
      });

      const { isConnected, currentSubscribedDeviceIds } = get();
      if (isConnected && (newDeviceIds.length > 0 || removedIds.length > 0)) {
        const removedSet = new Set(removedIds);
        const newSubscribedIds = [
          ...currentSubscribedDeviceIds.filter((id) => !removedSet.has(id)),
          ...newDeviceIds.filter((id) => !currentSubscribedDeviceIds.includes(id)),
        ];
        set({ currentSubscribedDeviceIds: newSubscribedIds });
        subscribeToDevices(newSubscribedIds);
        if (newDeviceIds.length > 0) {
          setTimeout(() => {
            getLastPositionBatch(newDeviceIds, false);
          }, 100);
        }
      }
    },

    updateEvent: (event: any) => {
      set((state) => {
        // Usar Map para lookup O(1)
        const vehicleIndex = state.vehiclesMap.get(event.device_id);

        if (vehicleIndex === undefined) {
          return { vehicles: state.vehicles };
        }

        const existingVehicle = state.vehicles[vehicleIndex];

        // Solo actualizar posición si tenemos coordenadas válidas
        const hasValidPosition = event.lat != null && event.lon != null;

        const eventParamsResult = mergeSocketParams(existingVehicle.socketParams, event.params);

        // Determinar nuevo estado si tenemos los datos necesarios
        let newStatus = existingVehicle.status;
        let newIdleSince = existingVehicle.idleSince ?? null;
        if (hasValidPosition && event.timeStamp) {
          const eventIgnition =
            event.engine_status != null
              ? event.engine_status === 1 ||
                event.engine_status === 2 ||
                event.engine_status === 3
              : existingVehicle.ignition;
          const eventEffectiveSpeed = resolveEffectiveSpeed(
            existingVehicle.speed,
            existingVehicle.odometer,
            event.odo_km ?? existingVehicle.odometer,
            existingVehicle.latitude,
            existingVehicle.longitude,
            event.lat,
            event.lon,
            existingVehicle.lastReport._seconds,
            event.timeStamp
          );
          const eventIdle = resolveMetricsFromParams(
            eventParamsResult.params,
            existingVehicle.brandModel,
            existingVehicle.brandName
          ).idle;
          const eventStatus = determineVehicleStatus(
            eventEffectiveSpeed,
            eventIgnition,
            event.timeStamp,
            existingVehicle.status,
            undefined,
            existingVehicle.idleSince,
            eventIdle
          );
          newStatus = eventStatus.status;
          newIdleSince = eventStatus.idleSince;
        }

        // Verificar si hay cambios visuales (posición o estado)
        const hasPositionChanged = hasValidPosition && (
          existingVehicle.latitude !== event.lat ||
          existingVehicle.longitude !== event.lon
        );
        const hasStatusChanged = existingVehicle.status !== newStatus;

        const newEventGpsSat = event.gps_sat ?? existingVehicle.gps_sat;
        const newEventEngineStatus = event.engine_status ?? existingVehicle.engineStatus;
        const newEventEventCode = event.event_code ?? existingVehicle.eventCode;
        const newEventSignal = event.rssi ?? existingVehicle.signal;
        const newEventOdometer = event.odo_km ?? existingVehicle.odometer;
        const newEventTimestamp = event.timeStamp ? timestampToSeconds(event.timeStamp) : existingVehicle.lastReport._seconds;

        // Mantener encendido el panic/SOS en la card cuando el dispositivo
        // emite "Pánico" (5) o "Alerta" (45) — aunque el backend aún no haya
        // escrito params.panic en el RTDB positions/{cid}/{did}.
        const PANIC_EVENT_CODES = new Set<number>([5, 45]);
        const eventCodeNum = Number(newEventEventCode);
        const isPanicEvent = Number.isFinite(eventCodeNum) && PANIC_EVENT_CODES.has(eventCodeNum);
        const newEventPanic = event.panic != null
          ? event.panic
          : isPanicEvent
            ? eventCodeNum
            : existingVehicle.params.panic;

        const hasEventDataChanged =
          existingVehicle.gps_sat !== newEventGpsSat ||
          existingVehicle.engineStatus !== newEventEngineStatus ||
          existingVehicle.eventCode !== newEventEventCode ||
          existingVehicle.signal !== newEventSignal ||
          existingVehicle.odometer !== newEventOdometer ||
          existingVehicle.lastReport._seconds !== newEventTimestamp ||
          existingVehicle.params.panic !== newEventPanic ||
          existingVehicle.idleSince !== newIdleSince ||
          eventParamsResult.changed;

        if (!hasPositionChanged && !hasStatusChanged && !hasEventDataChanged) {
          return { vehicles: state.vehicles };
        }

        const updatedVehicles = [...state.vehicles];
        updatedVehicles[vehicleIndex] = {
          ...existingVehicle,
          status: newStatus,
          idleSince: newIdleSince,
          ...(hasValidPosition && {
            coordinates: `${event.lat},${event.lon}`,
            latitude: event.lat,
            longitude: event.lon,
          }),
          lastReport: {
            _seconds: newEventTimestamp,
            _nanoseconds: 0,
          },
          gps_sat: newEventGpsSat,
          engineStatus: newEventEngineStatus,
          eventCode: newEventEventCode,
          signal: newEventSignal,
          odometer: newEventOdometer,
          ignition: event.engine_status != null
            ? event.engine_status === 1 || event.engine_status === 2 || event.engine_status === 3
            : existingVehicle.ignition,
          device: {
            ...existingVehicle.device,
            type: event.type ?? existingVehicle.device.type,
            mileage: event.odo_km ?? existingVehicle.device.mileage,
          },
          params: {
            ...existingVehicle.params,
            panic: newEventPanic,
            ...(event.timeStamp && {
              datetime: safeToISOString(event.timeStamp),
            }),
            ...(event.engine_status != null && {
              engineStatus: String(event.engine_status),
            }),
            ...(hasValidPosition && {
              latitude: event.lat,
            }),
          },
          vehicle_id: event.vehicle_id !== undefined ? (event.vehicle_id ?? "") : existingVehicle.vehicle_id,
          remolque_id: event.remolque_id !== undefined ? (event.remolque_id ?? "") : existingVehicle.remolque_id,
          socketParams: eventParamsResult.params,
          companyId: event.company_id || existingVehicle.companyId,
        };

        return {
          vehicles: updatedVehicles,
        };
      });
    },
    setConnectionStatus: (status) => set({ isConnected: status }),
    setAwaitingCompanySelection: (awaiting) => set({
      awaitingCompanySelection: awaiting,
      loading: false, // No mostrar loading cuando esperamos selección de compañía
    }),
    setLoading: (loading) => set({ loading }),
    setLoadError: (loadError) => set({ loadError }),
    requestReload: () =>
      set((state) => ({ reloadRequestId: state.reloadRequestId + 1 })),
    resetStore: () => {
      isInitialLoadPhase = false;
      initialLoadPositions = new Map();

      set({
        vehicles: [],
        vehiclesMap: new Map(),
        loading: true,
        error: null,
        loadError: null,
        isConnected: false,
        isInitialized: false,
        lastInitializedCompanyId: null,
        awaitingCompanySelection: false,
        isLoadingInitialPositions: false,
        initialSyncDone: false,
        pendingDevicesData: [],
        allCompanyDeviceIds: [],
        currentSubscribedDeviceIds: [],
        selectedGroup: "all",
        loadingAddressDeviceIds: {},
        loadingPositionDeviceIds: {},
      });
    },
  })
);